import { type ReactNode, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { Controls, Player } from '@lottiefiles/react-lottie-player';
import { useOktaAuth } from '@okta/okta-react';
import { useSelector } from 'react-redux';

import { isExternalIFrame } from '@/utils/iframe';

import type { User } from '../@types/global';
import { useExternalMagicLink } from '../modules/login/useExternalMagicLink';
import { useLogin } from '../modules/login/useLogin';
import { isPublicRoute } from '../utils/api';
import { useDirectorPracticeView } from '../utils/hooks/useDirectorPracticeView';

import { useHandleLogout } from './appHeader/utils';

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

/**
 * This higher order component ensures that before attempting render any routes
 * we first check if the user is already authenticated and then hydrate the user store
 * if necessary.
 */
export const HydrateUser = ({ children }: { children: ReactNode }) => {
  const [isAuthenticatedChecked, setIsAuthenticatedChecked] = useState<
    boolean | undefined
  >(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
    undefined,
  );
  const user: User = useSelector((state: any) => state?.login?.user);
  const { isDirectorPracticeViewUser } = useDirectorPracticeView();

  const { oktaAuth, authState } = useOktaAuth();
  const { success, loginAllTabs } = useLogin();
  const { isFetchingTokens, tokens } = useExternalMagicLink();
  const { logoutAllTabs, logout } = useHandleLogout();

  useEffect(() => {
    // Skip authentication logic for public routes to prevent cross-tab contamination
    if (isPublicRoute()) {
      setIsAuthenticatedChecked(true);
      setIsAuthenticated(false);
      return;
    }

    // Skip cross-tab communication for external iframe users to prevent interference
    // when multiple iframes are loaded with the same magic key
    if (!isExternalIFrame()) {
      logoutAllTabs();
      loginAllTabs();
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'logoutAllTabs'. Either include it or remove the dependency array. */
  }, []);

  useEffect(() => {
    // Skip authentication check for public routes
    if (isPublicRoute()) {
      return;
    }

    if (isFetchingTokens) {
      return;
    }
    oktaAuth
      .isAuthenticated()
      .then((v) => setIsAuthenticated(v))
      .finally(() => setIsAuthenticatedChecked(true));
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'oktaAuth'. Either include it or remove the dependency array. */
  }, [isFetchingTokens]);

  const idToken = oktaAuth.getIdToken();
  useEffect(() => {
    // Skip user login for public routes
    if (isPublicRoute()) {
      return;
    }

    if (
      !user?.email &&
      idToken &&
      isAuthenticated &&
      !tokens &&
      !isFetchingTokens
    ) {
      success(idToken).catch(() => logout());
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'success'. Either include it or remove the dependency array. */
  }, [user?.email, idToken, isAuthenticated, tokens, isFetchingTokens]);

  // Skip loading check for public routes
  if (isPublicRoute()) {
    return children;
  }

  // If we are still fetching the user OR org/practice state, return a circular progress
  if (
    isFetchingTokens ||
    !isAuthenticatedChecked ||
    (isAuthenticatedChecked && authState?.isAuthenticated && !user?.username) ||
    (isAuthenticatedChecked &&
      authState?.isAuthenticated &&
      isDirectorPracticeViewUser &&
      !user?.primaryPracticeId)
  ) {
    return isExternalIFrame() ? (
      <div className="vai-h-full vai-bg-sidebar-background vai-flex vai-items-center vai-justify-center">
        <div className="vai-animate-spin vai-rounded-full vai-h-8 vai-w-8 vai-border-[3px] vai-border-t-transparent vai-border-sapphire-600" />
      </div>
    ) : (
      <LoadingContainer>
        <Player
          autoplay
          loop
          src="/assets/images/videa-load.json"
          style={{ height: '340px', width: '600px' }}
        >
          <Controls visible={false} buttons={[]} />
        </Player>
      </LoadingContainer>
    );
  }
  return children;
};
